import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clsx } from 'clsx';

import { Alert, Button, Icon, Pagination, Table } from '@treezor/design-system';

import { Page } from '@/ui-kit/templates/Page';
import { openModal } from '@/store/actions/core.actions';
import { ICreateDashboardUserProps } from '@/features/dashboard-users/components/create/CreateDashboardUserModal';
import { useDashboardUsersTable } from '@/features/dashboard-users/hooks/useDashboardUsersTable';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';
import { DashboardUsersFilters } from '@/features/dashboard-users/components/DashboardUsersFilters';
import { ManageDashboardUserModal } from '@/features/dashboard-users/components/manage/ManageDashboardUserModal';
import { ISupportUser } from '@/features/dashboard-users/store/support-user.model';

export const PER_PAGE_USERS = 100;

export function DashboardUsers() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedDashboarduser, setSelectedDashboarduser] = useState<ISupportUser>();

  const onManageDashboardUser = useCallback((dashboardUser: ISupportUser) => {
    setSelectedDashboarduser(dashboardUser);
    setIsMenuOpen(true);
  }, []);

  const {
    cursor,
    rows,
    columns,
    pagination,
    shouldDisplayReviewNotice,
    handleCompleteReview,
    fields,
    hasFilters,
    suspendedDashboardUsers,
    isActiveDashboardUserDisplayed,
    onNavigateToActiveDashboardUser,
    onNavigateToSuspendedDashboardUser,
    clearFilters,
  } = useDashboardUsersTable(onManageDashboardUser);

  return (
    <Page
      {...(isActiveDashboardUserDisplayed
        ? {
            actions: [
              {
                id: 'add-support-user-btn',
                label: t('pages.administration.supportsUsers.tooltip.create'),
                icon: 'add',
                onClick: () => dispatch(openModal<ICreateDashboardUserProps>('createDashboardUserModal')),
              },
            ],
          }
        : {})}
      title={
        isActiveDashboardUserDisplayed
          ? t('pages.administration.supportsUsers.title')
          : t('pages.administration.supportsUsers.suspendedDashboardUsers.title')
      }
      {...(!isActiveDashboardUserDisplayed
        ? {
            backAction: {
              onClick: () => onNavigateToActiveDashboardUser(),
              children: t('pages.administration.supportsUsers.suspendedDashboardUsers.backToDashboardUsers'),
              variant: 'ghost',
            },
          }
        : {})}
    >
      {shouldDisplayReviewNotice && (
        <Alert
          className="mb-4"
          type="default"
          variant="secondary"
          title=""
          description={t('pages.cores.warnings.reviewUsersRoles.alert.description')}
          action={{ title: t('pages.cores.warnings.reviewUsersRoles.alert.confirm'), onClick: handleCompleteReview }}
        />
      )}

      <div className="flex items-center justify-between">
        <DashboardUsersFilters
          fields={fields}
          isActiveDashboardUserDisplayed={isActiveDashboardUserDisplayed}
          clearFunction={hasFilters ? () => clearFilters() : undefined}
        />
        {isActiveDashboardUserDisplayed && !!suspendedDashboardUsers?.length && (
          <Button variant="ghost" size="s" onClick={onNavigateToSuspendedDashboardUser}>
            {`${t('pages.administration.supportsUsers.suspendedDashboardUsers.manage')} (${suspendedDashboardUsers?.length})`}
            <Icon name="arrow_right_alt" />
          </Button>
        )}
      </div>

      <Table
        hasStickyHeader={true}
        columns={columns}
        rows={rows}
        className={clsx(
          'lightScrollBar',
          shouldDisplayReviewNotice ? 'h-[calc(100vh-410px)]' : 'h-[calc(100vh-320px)]',
        )}
        emptyComponent={
          <EmptyState
            title={t('components.molecules.table.noRecord')}
            icon="search_v1"
            size="m"
            className="p-6"
            action={t('components.molecules.table.tryClearFilters')}
            onClick={hasFilters ? () => clearFilters() : undefined}
            buttonIcon={null}
          />
        }
      ></Table>

      {(!!cursor.next || !!cursor.prev) && (
        <Pagination
          {...pagination}
          isFirstButtonVisible
          isLastButtonVisible={false}
          isPagesVisible={false}
          className="flex justify-end"
          isDisabled={false}
        />
      )}

      {isMenuOpen && selectedDashboarduser && (
        <ManageDashboardUserModal
          onClose={() => setIsMenuOpen(false)}
          isModalOpen={isMenuOpen}
          dashboardUserId={selectedDashboarduser.id}
        />
      )}
    </Page>
  );
}

export default DashboardUsers;
